/**
 * Daily caps screen (v2 — Project Independence, DNKADM-0057 U1) — the scope-keyed
 * daily-cap matrix as a list of cap rows + Add/Edit dialog (handoff §5.11). Replaces
 * the v1 tiers×buckets grid; the v1 global daily-cap hours setting is gone (§9.5).
 *
 * Each row is one `v2_daily_cap`: an "Applies to" label (All bookings / Plan X /
 * Bucket Y / Plan X + Bucket Y, resolved from the 0056 plan + 0052 bucket lists) and
 * its `capHours`. The list explains the model: **no matching cap = uncapped**, and a
 * booking obeys the **most specific** matching cap (`Plan + bucket` beats `Plan` beats
 * `Bucket` beats `All`). A duplicated `(plan, bucket)` scope is flagged with a warning
 * (warn, never block); the unique-scope `409` surfaces in the dialog on save.
 *
 * "Add cap" and Edit open the {@link CapDialog}. **Every write — create, update,
 * delete — refetches the list** (refetch-after-write); a `404` on delete just
 * refreshes (the cap was already gone); any other delete failure raises a toast.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds an
 * "Applies to" search; Applies to and Cap have shared {@link SortableHeader}s. The default
 * stays the precedence order (Applies to ascending, see `dailyCapsListView.ts`). Search
 * and sort live in the URL.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { matchesSearch } from '@/components/list/listSearch';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { ApiError } from '@/api/errors';
import { serverMessage } from '@/api/serverMessage';
import { formatHours } from '@/utils/v2Format';
import { deleteDailyCap, listDailyCaps, type DailyCap } from '@/api/dailyCaps';
import { listMembershipPlans, type MembershipPlan } from '@/api/membershipPlans';
import { listCreditBuckets, type CreditBucket } from '@/api/creditBuckets';
import { capKey, duplicateKeys } from './capScope';
import { CapDialog } from './CapDialog';
import {
  DAILY_CAP_DEFAULT_SORT,
  DAILY_CAP_SORT_KEYS,
  DAILY_CAPS_URL_DEFAULTS,
  dailyCapSortValue,
  type DailyCapSortKey,
} from './dailyCapsListView';
import { ScreenError } from '@/components/ScreenError';

/** Resolve a scoped id to a display name via a lookup map, falling back to the id. */
function nameOr(id: string, byId: Map<string, string>): string {
  return byId.get(id) ?? id;
}

/**
 * The "Applies to" label for a cap, resolving plan/bucket ids to names. Mirrors the
 * derived scope: `All bookings` / `Plan X` / `Bucket Y` / `Plan X + Bucket Y`.
 */
function appliesToLabel(
  cap: DailyCap,
  planNameById: Map<string, string>,
  bucketNameById: Map<string, string>,
): string {
  const plan =
    cap.membershipPlanId !== null
      ? `Plan ${nameOr(cap.membershipPlanId, planNameById)}`
      : null;
  const bucket =
    cap.creditBucketId !== null
      ? `Bucket ${nameOr(cap.creditBucketId, bucketNameById)}`
      : null;
  if (plan && bucket) return `${plan} + ${bucket}`;
  if (plan) return plan;
  if (bucket) return bucket;
  return 'All bookings';
}

export function DailyCapsScreen() {
  const showToast = useToast();
  // DNKADM-0430: search and sort come from the URL (`dailyCapsListView.ts`).
  const url = useListUrlState(DAILY_CAPS_URL_DEFAULTS);
  const setUrl = url.set;
  const search = url.values.q;
  const sort = parseSort(
    url.values.sort,
    url.values.dir,
    DAILY_CAP_SORT_KEYS,
    DAILY_CAP_DEFAULT_SORT,
  );
  const caps = useResource(useCallback(() => listDailyCaps(), []));

  // Resolve plan/bucket ids → names for the labels. Load `all` so a cap scoped to a
  // now-inactive plan/bucket still resolves. A failure degrades to showing the raw id;
  // it never blocks the list.
  const plans = useResource(useCallback(() => listMembershipPlans({ status: 'all' }), []));
  const buckets = useResource(useCallback(() => listCreditBuckets({ status: 'all' }), []));

  const planNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of (plans.data ?? []) as MembershipPlan[]) {
      map.set(p.id, p.name.length > 0 ? p.name : p.id);
    }
    return map;
  }, [plans.data]);

  const bucketNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of (buckets.data ?? []) as CreditBucket[]) {
      map.set(b.id, b.name.length > 0 ? b.name : b.id);
    }
    return map;
  }, [buckets.data]);

  // Dialog state: `null` = closed; `{ cap: null }` = add; `{ cap }` = edit.
  const [dialog, setDialog] = useState<{ cap: DailyCap | null } | null>(null);

  const refetch = caps.refetch;

  // The rows in view: the search on the "Applies to" label, then the column sort (ties on
  // the label, so caps of one scope keep a stable order).
  const rows = useMemo(() => {
    const label = (cap: DailyCap) => appliesToLabel(cap, planNameById, bucketNameById);
    return sortRows(
      (caps.data ?? []).filter((cap) => matchesSearch(search, [label(cap)])),
      (cap) => dailyCapSortValue(cap, sort.key),
      sort.dir,
      label,
    );
  }, [caps.data, planNameById, bucketNameById, search, sort.key, sort.dir]);
  const onSort = useCallback(
    (key: DailyCapSortKey) =>
      setUrl(toSortParams(toggleSort(sort, key), DAILY_CAP_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const duplicates = useMemo(() => duplicateKeys(caps.data ?? []), [caps.data]);

  const handleDelete = useCallback(
    async (cap: DailyCap) => {
      try {
        await deleteDailyCap(cap.id);
      } catch (err) {
        // A 404 means the cap was already gone — just refresh the list (no error).
        if (!(err instanceof ApiError && err.code === 'NOT_FOUND')) {
          const message = serverMessage(err) ?? 'The cap couldn’t be deleted. Please try again.';
          showToast(message, 'error');
          return;
        }
      }
      refetch();
    },
    [refetch, showToast],
  );

  if (caps.status === 'loading') {
    return (
      <Skeleton
        variant="rounded"
        height={300}
        role="status"
        aria-label="Loading daily caps"
        sx={{ borderRadius: 3 }}
      />
    );
  }

  if (caps.status === 'error' || !caps.data) {
    return (
      <ScreenError
        error={caps.error}
        what="the daily caps"
        onRetry={caps.refetch}
        sx={{ alignItems: 'center' }}
      />
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
      >
        <Typography variant="h6" component="h2">
          Daily caps
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setDialog({ cap: null })}
        >
          Add cap
        </Button>
      </Stack>
      <Alert severity="info" icon={<Icon name="info" />} sx={{ mb: 2 }}>
        Each cap limits the hours per day a member may book. A booking obeys the single
        most specific matching cap: a plan + bucket cap beats a plan cap, which beats a
        bucket cap, which beats an all-bookings cap. When no cap matches, the booking is
        uncapped.
      </Alert>
      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search daily caps',
        }}
        onClear={() => setUrl({ q: null })}
      />
      {caps.data.length === 0 ? (
        <Alert severity="info" icon={<Icon name="schedule" />}>
          No daily caps yet. Every booking is uncapped until you add one.
        </Alert>
      ) : (
        <TableContainer>
          <Table aria-label="Daily caps" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Applies to" col="appliesTo" sort={sort} onSort={onSort} />
                <SortableHeader label="Cap" col="cap" align="right" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((cap) => {
                const label = appliesToLabel(cap, planNameById, bucketNameById);
                const duplicated = duplicates.has(capKey(cap.membershipPlanId, cap.creditBucketId));
                return (
                  <TableRow key={cap.id}>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <span>{label}</span>
                        {duplicated && (
                          <Tooltip title="Another cap covers this same scope.">
                            <Box component="span" sx={{ display: 'inline-flex', color: 'warning.main' }}>
                              <Icon name="warning" title="Duplicate cap scope" sx={{ fontSize: 18 }} />
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatHours(cap.capHours)} hrs/day</TableCell>
                    <TableCell align="right">
                      <IconAction
                        size="small"
                        label={`Edit cap ${label}`}
                        onClick={() => setDialog({ cap })}
                      >
                        <Icon name="edit" />
                      </IconAction>
                      <IconAction
                        size="small"
                        label={`Delete cap ${label}`}
                        onClick={() => void handleDelete(cap)}
                      >
                        <Icon name="delete" />
                      </IconAction>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {caps.data.length > 0 && rows.length === 0 && (
        <ListNoMatch noun="daily caps" query={search} />
      )}
      <CapDialog
        open={dialog !== null}
        cap={dialog?.cap ?? null}
        caps={caps.data}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
